import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  FlyToBoundsRefProps,
  IAnimationState,
} from "../components/Leaflet/FlyToBounds/Index";
import { useFilter } from "./useFilter";
import { getCenterCoords } from "../utils/leaflet/getCenterCoords";
import { ICoords } from "../@types/GoogleMaps/ICoord";
import { IFarms } from "../@types/API/IFarm";
import { IBatch } from "../@types/API/IBatches";
import { useData } from "./useData";
import { IOptionProps } from "../@types/reactSelect/IOption";
import {
  flattenSelection,
  selectionToSortedIdsKey,
  useFilterContextV2,
} from "../contexts/FilterContextV2";

type ICustomFarm = {
  id: string;
  initials: string;
  coords: ICoords;
  name: string;
  bgColor: string;
};

type ICustomArea = {
  id: string;
  initials: string;
  coords: ICoords;
  name: string;
  bgColor: string;
  farmId?: string;
  batches: Array<{
    visualization: boolean;
    coord: ICoords | undefined;
    microAreaId: string;
  }>;
};

function optionToMapFarmFilter(opt: IOptionProps | undefined): ICustomFarm | undefined {
  if (!opt?.info) return undefined;
  const farm = opt.info as {
    id?: number | string;
    map_coords?: ICoords;
    initials?: string;
    name?: string;
  };
  const coords = farm.map_coords;
  if (!coords || !Array.isArray(coords) || coords.length === 0) return undefined;
  return {
    id: String(farm.id),
    initials: String(farm.initials ?? ""),
    coords,
    name: String(farm.name ?? ""),
    bgColor: "",
  };
}

function optionToMapAreaFilter(opt: IOptionProps | undefined): ICustomArea | undefined {
  if (!opt?.info) return undefined;
  const area = opt.info as IArea;
  const coords = area.map_coords;
  if (!coords || !Array.isArray(coords) || coords.length === 0) return undefined;
  return {
    id: String(area.id),
    initials: String(area.initials ?? ""),
    coords,
    name: String(area.name ?? ""),
    bgColor: "",
    farmId: area.farm?.id != null ? String(area.farm.id) : undefined,
    batches: [],
  };
}

function buildBatchIconsFromCatalog(batches: IBatch[]) {
  return batches.map((batch) => {
    const microAreaId = Number(batch.current_micro_area.id);
    return {
      initials: batch.initials,
      coords: getCenterCoords(batch.current_micro_area.map_coords),
      id: batch.id.toString(),
      farmId: String(batch.farm.id),
      areaId: batch.area.id.toString(),
      visible: true,
      currentMicroArea: microAreaId,
    };
  });
}

export function usePaddockMapData() {
  const flytoBoundsRef = useRef<FlyToBoundsRefProps>(null);
  const [stateOfAnimation, setStateOfAnimation] =
    useState<IAnimationState>("end");
  const [errorModal, setErrorModal] = useState<boolean>(false);

  const { customBatches: batchesIcon, setCustomBatches, setBatches, microAreas, setMicroAreas } = useData();

  const {
    microAreaFilter,
    batchFilter,
    setHideMicroAreaFilter,
    setHideBatchFilter,
    responseFarms,
    responseArea,
    paddockMapCatalogLoadError,
    setPaddockMapCatalogLoadError,
  } = useFilter();

  const { selectedFarms, selectedAreas, catalogMicroAreas, catalogBatches } =
    useFilterContextV2();

  const farmsKey = useMemo(
    () => selectionToSortedIdsKey(selectedFarms),
    [selectedFarms],
  );
  const areasKey = useMemo(
    () => selectionToSortedIdsKey(selectedAreas),
    [selectedAreas],
  );

  const mapFarmFilter = useMemo(() => {
    const first = flattenSelection(selectedFarms)[0];
    return optionToMapFarmFilter(first);
  }, [selectedFarms, farmsKey]);

  const mapAreaFilter = useMemo(() => {
    const first = flattenSelection(selectedAreas)[0];
    return optionToMapAreaFilter(first);
  }, [selectedAreas, areasKey]);

  const batchByMicroAreaMap = useMemo(() => {
    const map = new Map<number, IBatch>();
    if (!mapFarmFilter) return map;
    const fid = mapFarmFilter.id;
    for (const batch of catalogBatches) {
      if (String(batch.farm.id) !== fid) continue;
      const microAreaId = Number(batch.current_micro_area.id);
      map.set(microAreaId, batch);
    }
    return map;
  }, [catalogBatches, mapFarmFilter?.id]);

  // Função para verificar visualização de batch
  const handleBatchVisualization = useCallback(
    (farmId: string, areaId: string, microAreaId?: string) => {
      if (batchFilter && mapFarmFilter && farmId === mapFarmFilter.id && mapAreaFilter)
        return (
          mapAreaFilter.id === areaId && microAreaId === batchFilter.microAreaId
        );
      if (mapAreaFilter && mapFarmFilter && farmId === mapFarmFilter.id)
        return mapAreaFilter.id === areaId;
      return true;
    },
    [batchFilter, mapAreaFilter, mapFarmFilter],
  );

  // Centralizar mapa
  const centerMap = useCallback(
    (coords: ICoords) => {
      if (flytoBoundsRef.current?.canZoomAnimate(coords, 50)) {
        flytoBoundsRef.current.flyToBounds(coords, {
          duration: mapAreaFilter ? 0.5 : 5,
        });
      } else {
        setStateOfAnimation("end");
      }
    },
    [mapAreaFilter],
  );

  const farms = useMemo(
    () => responseFarms as unknown as IFarms,
    [responseFarms],
  );

  const areas = responseArea;

  const showDataErrorModal = errorModal || paddockMapCatalogLoadError;

  useEffect(() => {
    if (!mapFarmFilter) {
      setMicroAreas([]);
      setBatches([]);
      setCustomBatches([]);
      return;
    }
    const fid = mapFarmFilter.id;
    const micro = catalogMicroAreas.filter(
      (m) => String(m.farm?.id) === fid,
    );
    const bat = catalogBatches.filter((b) => String(b.farm.id) === fid);
    setMicroAreas(micro);
    setBatches(bat);
    setCustomBatches(buildBatchIconsFromCatalog(bat));
  }, [
    mapFarmFilter?.id,
    catalogMicroAreas,
    catalogBatches,
    setMicroAreas,
    setBatches,
    setCustomBatches,
  ]);

  const memoizedData = useMemo(
    () => ({
      farms,
      areas,
      microAreas,
      batchesIcon,
      batchByMicroAreaMap,
      errorModal: showDataErrorModal,
      stateOfAnimation,
    }),
    [
      farms,
      areas,
      microAreas,
      batchesIcon,
      batchByMicroAreaMap,
      showDataErrorModal,
      stateOfAnimation,
    ],
  );

  useEffect(() => {
    if (!mapFarmFilter) return;
    centerMap(mapFarmFilter.coords);
  }, [farmsKey, mapFarmFilter?.id]);

  useEffect(() => {
    if (mapAreaFilter) {
      centerMap(mapAreaFilter.coords);
    }
  }, [areasKey, mapAreaFilter?.id, centerMap]);

  useEffect(() => {
    setHideMicroAreaFilter(false);
    setHideBatchFilter(false);
  }, [setHideMicroAreaFilter, setHideBatchFilter]);

  const toggleErrorModal = useCallback(() => {
    setErrorModal(false);
    setPaddockMapCatalogLoadError(false);
  }, [setPaddockMapCatalogLoadError]);

  return {
    ...memoizedData,
    mapFarmFilter,
    mapAreaFilter,
    flytoBoundsRef,
    setStateOfAnimation,
    centerMap,
    toggleErrorModal,
    handleBatchVisualization,
  };
}
